/* BuildProfilePage — Public Build Badge profile.
   /builds/profile/:ident → fetches /api/builds/profile/:ident */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Github, ExternalLink, Video, Sparkles, ShieldCheck, Share2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_STYLE = {
  bronze: { bg: 'from-orange-400 to-red-500',     chip: 'bg-orange-100 text-orange-800' },
  silver: { bg: 'from-slate-400 to-slate-600',    chip: 'bg-slate-100 text-slate-800' },
  gold:   { bg: 'from-amber-400 to-orange-600',   chip: 'bg-amber-100 text-amber-800' },
};

const shareToLinkedIn = (url, text) => {
  const u = encodeURIComponent(url);
  window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${u}`, '_blank', 'noopener');
};

const shareToWhatsApp = (text, url) => {
  const msg = encodeURIComponent(`${text}\n${url}`);
  window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener');
};

const BuildProfilePage = () => {
  const { ident } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/builds/profile/${ident}`);
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Profile not found.');
      }
    })();
  }, [ident]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <main className="pt-32 text-center text-gray-600 px-4">{err}</main>
        <Footer />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <main className="pt-32 text-center text-gray-600 px-4">Loading…</main>
        <Footer />
      </div>
    );
  }

  const profileUrl = typeof window !== 'undefined' ? window.location.href : '';
  const user = data.user;
  const initials = (user.name || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  // Prefer the username-slug URL over the long user_id URL for sharing
  const cleanUrl = (typeof window !== 'undefined' && user.slug)
    ? `${window.location.origin}/builds/profile/${user.slug}`
    : profileUrl;
  const copyClean = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(cleanUrl).catch(() => {});
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9F1]">
      <Navbar />
      <main className="pt-24 md:pt-28 pb-16 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Profile card */}
          <section className="bg-white rounded-3xl border-2 border-amber-200 shadow-xl p-6 md:p-8 mb-8 flex flex-col sm:flex-row items-center sm:items-start gap-5" data-testid="profile-card">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-amber-500 to-orange-600 text-white font-black text-2xl flex items-center justify-center shadow-lg shrink-0">
              {initials}
            </div>
            <div className="flex-1 text-center sm:text-left">
              <span className="text-[10px] font-extrabold tracking-widest uppercase text-amber-700">LearnixTa verified builder</span>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 mt-1">{user.name}</h1>
              <p className="text-sm text-gray-600 mt-1">
                {data.badge_count} verified build{data.badge_count === 1 ? '' : 's'}{user.stage ? ` · ${user.stage}` : ''}
              </p>
              <div className="flex flex-wrap gap-2 mt-4 justify-center sm:justify-start">
                <button onClick={() => shareToLinkedIn(cleanUrl, `Check out ${user.name}'s verified builds on LearnixTa`)} data-testid="share-linkedin" className="inline-flex items-center gap-1.5 text-sm font-bold bg-[#0A66C2] text-white px-3 py-1.5 rounded-full hover:opacity-90">
                  <Share2 size={13} /> Share on LinkedIn
                </button>
                <button onClick={() => shareToWhatsApp(`${user.name} just earned a Build Badge on LearnixTa`, cleanUrl)} data-testid="share-whatsapp" className="inline-flex items-center gap-1.5 text-sm font-bold bg-[#25D366] text-white px-3 py-1.5 rounded-full hover:opacity-90">
                  <Share2 size={13} /> WhatsApp
                </button>
                <button onClick={copyClean} data-testid="share-copy" className="inline-flex items-center gap-1.5 text-sm font-bold bg-gray-900 hover:bg-gray-700 text-white px-3 py-1.5 rounded-full">
                  Copy link
                </button>
              </div>
              {user.slug && (
                <p className="mt-2 text-[11px] text-gray-500" data-testid="profile-clean-url">
                  Permanent link · <code className="bg-gray-100 px-1.5 py-0.5 rounded">{cleanUrl.replace(/^https?:\/\//, '')}</code>
                </p>
              )}
            </div>
          </section>

          {/* Empty state */}
          {data.badge_count === 0 && (
            <div className="bg-white rounded-3xl border-2 border-dashed border-amber-300 p-8 text-center" data-testid="profile-empty">
              <Trophy size={32} className="text-amber-500 mx-auto mb-3" />
              <h3 className="text-lg font-extrabold text-gray-900 mb-1">No badges yet.</h3>
              <p className="text-sm text-gray-600 mb-4 max-w-sm mx-auto">
                Submit your first build to earn a Bronze Build Badge — and turn this profile into proof recruiters can verify.
              </p>
              <Link to="/build-badges" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-gray-900 font-extrabold px-5 py-2.5 rounded-full text-sm">
                Browse all briefs →
              </Link>
            </div>
          )}

          {/* Badge cards */}
          <div className="space-y-4">
            {data.badges.map((b) => {
              const T = TIER_STYLE[b.badge_tier] || TIER_STYLE.bronze;
              return (
                <article key={b.id} data-testid={`badge-${b.id}`} className="bg-white rounded-3xl border-2 border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  <div className={`h-1.5 bg-gradient-to-r ${T.bg}`} />
                  <div className="p-6 md:p-7">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold tracking-widest uppercase ${T.chip} px-2 py-1 rounded-full`}>
                          <Trophy size={11} /> {b.badge_tier} · {b.department}
                        </span>
                        <h3 className="font-extrabold text-gray-900 text-xl mt-2">{b.role_name}</h3>
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full font-bold">
                        <ShieldCheck size={11} /> Verified
                      </span>
                    </div>
                    {b.review_summary && (
                      <p className="text-[14px] text-gray-700 leading-relaxed mt-2">{b.review_summary}</p>
                    )}
                    {b.scores && (
                      <div className="grid grid-cols-4 gap-2 mt-4">
                        {Object.entries(b.scores).map(([k, v]) => (
                          <div key={k} className="bg-gray-50 rounded-xl p-2 text-center">
                            <div className="text-lg font-black text-gray-900">{v}<span className="text-xs text-gray-400">/10</span></div>
                            <div className="text-[9px] font-bold tracking-widest uppercase text-gray-500 mt-0.5">{k.replace('_',' ')}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-4 text-xs">
                      {b.github_url && (
                        <a href={b.github_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 hover:border-gray-300 text-gray-700">
                          <Github size={12} /> Repo
                        </a>
                      )}
                      {b.deployed_url && (
                        <a href={b.deployed_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 hover:border-gray-300 text-gray-700">
                          <ExternalLink size={12} /> Live
                        </a>
                      )}
                      {b.video_url && (
                        <a href={b.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 hover:border-gray-300 text-gray-700">
                          <Video size={12} /> Walkthrough
                        </a>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-3 flex items-center gap-1">
                      <Sparkles size={11} /> AI-reviewed · earned {b.reviewed_at ? new Date(b.reviewed_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BuildProfilePage;
